import { useQuery } from "@tanstack/react-query";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Contact } from "../types";

export type MarthaDraftProposal = {
  draft_type?: string;
  channel?: string;
  list_id?: number;
  sending_identity?: string;
  door_category?: string;
  subject?: string;
  body?: string;
  socratic_stage?: number;
  suggested_send_at?: string;
  martha_agent_run_id?: string;
};

export type MarthaDraftResolution = {
  action?: "approved" | "rejected" | "edited_and_approved";
  final_body?: string;
  resolved_by?: string;
  resolved_at?: string;
};

export type MarthaDraft = {
  id: string;
  status: "pending" | "approved" | "rejected" | "needs_review";
  proposal: MarthaDraftProposal | null;
  resolution: MarthaDraftResolution | null;
};

export type MarthaReply = {
  sentiment: string | null;
  reply_summary: string | null;
  reply_type: string | null;
  raw_payload: Record<string, unknown> | null;
  received_at: string;
};

export type MarthaCardData = {
  draft: MarthaDraft | null;
  reply: MarthaReply | null;
};

/**
 * PRD #16 §E — Martha card data. Prefers the point read via
 * lead_snapshots.martha_last_queue_id (no scan); falls back to the newest
 * queue_type='martha_outreach_draft' row for the contact when the snapshot has
 * no queue id yet (e.g. before Marco/Martha writes are wired, or a brand-new draft
 * not yet reflected in the snapshot). Also reads the newest email_replies row —
 * per the 2026-09-08 checkpoint finding, this table currently only holds
 * auto-detected-unsubscribe rows with contact_id=NULL, so `reply` will correctly
 * be null for every contact until a real SK-04-classified reply exists.
 */
export function useMarthaDraft(contact: Contact | undefined) {
  const contactId = contact ? Number(contact.id) : undefined;

  return useQuery({
    queryKey: ["martha-draft", contactId, (contact as any)?.martha_last_queue_id],
    enabled: !!contactId,
    queryFn: async (): Promise<MarthaCardData> => {
      const mentorax = getSupabaseClient().schema("mentorax");
      const snapshotQueueId = (contact as any)?.martha_last_queue_id as
        | string
        | undefined;

      const draftQuery = snapshotQueueId
        ? mentorax
            .from("approval_queue")
            .select("id, status, resolution_payload")
            .eq("id", snapshotQueueId)
            .maybeSingle()
        : mentorax
            .from("approval_queue")
            .select("id, status, resolution_payload")
            .eq("contact_id", contactId)
            .eq("queue_type", "martha_outreach_draft")
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();

      const [draftRes, replyRes] = await Promise.all([
        draftQuery,
        mentorax
          .from("email_replies")
          .select("sentiment, reply_summary, reply_type, raw_payload, received_at")
          .eq("contact_id", contactId)
          .order("received_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      if (draftRes.error) throw draftRes.error;
      if (replyRes.error) throw replyRes.error;

      const draft = draftRes.data
        ? {
            id: draftRes.data.id,
            status: draftRes.data.status,
            proposal:
              (draftRes.data.resolution_payload as any)?.proposal ?? null,
            resolution:
              (draftRes.data.resolution_payload as any)?.resolution ?? null,
          }
        : null;

      return {
        draft,
        reply: (replyRes.data as MarthaReply) ?? null,
      };
    },
  });
}
